/**
 * Toggle a permission level for a feature in a Set of permission strings.
 *
 * Permission format: `feature:<key>:<level>` (e.g. `feature:videos:view`).
 *
 * Behavior:
 *   - `'none'`  → removes both `:view` and `:edit` keys
 *   - `'edit'`  → toggles edit; when activating, also adds view (edit implies view)
 *   - `'view'`  → toggles view; when activating, does NOT add edit
 *
 * Returns a new Set (immutable update).
 */
export function togglePermission(
  current: Set<string>,
  featureKey: string,
  level: 'view' | 'edit' | 'none',
): Set<string> {
  const next = new Set(current);
  const viewKey = `feature:${featureKey}:view`;
  const editKey = `feature:${featureKey}:edit`;

  if (level === 'none') {
    next.delete(viewKey);
    next.delete(editKey);
  } else if (level === 'edit') {
    if (next.has(editKey)) {
      next.delete(editKey);
      next.delete(viewKey);
    } else {
      next.add(editKey);
      next.add(viewKey);
    }
  } else {
    if (next.has(viewKey)) {
      next.delete(viewKey);
      next.delete(editKey);
    } else {
      next.add(viewKey);
    }
  }

  return next;
}

/**
 * Derive the effective display value for a feature from a permissions Set.
 */
export function getPermissionValue(
  permissions: Set<string>,
  featureKey: string,
): 'none' | 'view' | 'edit' {
  if (permissions.has(`feature:${featureKey}:edit`)) return 'edit';
  if (permissions.has(`feature:${featureKey}:view`)) return 'view';
  return 'none';
}
